import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { AuthService } from '@echospaces-web-monorepo/es-auth';
import {
  ButtonComponent,
  ContactCardComponent,
  EmptyStateComponent,
  EsnToastService,
  FaqFinderComponent,
  FaqGroupComponent,
  FeedbackFormComponent,
  PageHeaderComponent,
  type EsnContactFact,
  type EsnFeedbackPayload,
} from '@echospaces-web-monorepo/es-ui-next';

import { ACTIVE_COMPANY } from '../active-company.token';
import { PortalTourService } from '../tour/portal-tour.service';
import { HELP_CONTACT, HELP_FAQ, HELP_FEEDBACK_KINDS, countHelpFaq, filterHelpFaq } from './help-center.data';

const SUPER_ADMIN_ROLE = 'super-admin';

/**
 * Help Center (Figma "Help Center"): searchable FAQ by subject, the feedback form and the
 * "Talk to us" card with the facts support asks for and a way back into the guided tour.
 */
@Component({
  selector: 'es-help-center-page',
  imports: [
    ButtonComponent,
    ContactCardComponent,
    EmptyStateComponent,
    FaqFinderComponent,
    FaqGroupComponent,
    FeedbackFormComponent,
    PageHeaderComponent,
  ],
  templateUrl: './help-center-page.component.html',
  styleUrl: './help-center-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpCenterPageComponent {
  private readonly auth = inject(AuthService);
  private readonly toast = inject(EsnToastService);
  private readonly tour = inject(PortalTourService);
  private readonly company = inject(ACTIVE_COMPANY, { optional: true });

  protected readonly contact = HELP_CONTACT;
  protected readonly kinds = [...HELP_FEEDBACK_KINDS];

  protected readonly query = signal('');

  /**
   * Open questions across every subject; the first one starts open, as in Figma.
   */
  protected readonly open = signal<string[]>(['island-what']);

  protected readonly searching = computed(() => !!this.query().trim());
  protected readonly subjects = computed(() => filterHelpFaq(HELP_FAQ, this.query()));
  protected readonly matchCount = computed(() => countHelpFaq(this.subjects()));
  protected readonly countLabel = computed(() =>
    this.matchCount() === 1 ? '1 answer matches' : `${this.matchCount()} answers match`);

  protected readonly feedbackKind = signal<string | null>('problem');
  protected readonly feedbackSubject = signal('');
  protected readonly feedbackDetails = signal('');

  protected readonly replyTo = computed(() => this.auth.user()?.email ?? '');

  /**
   * Who is asking, for which organisation and with what reach — what support needs first.
   */
  protected readonly facts = computed<EsnContactFact[]>(() => {
    const user = this.auth.user();
    if (!user) return [];
    const organisation = this.company?.resourceId ?? user.organizationId ?? '—';
    const scope = user.roles.includes(SUPER_ADMIN_ROLE) ? 'Every space (root)' : 'Spaces shared with you';
    return [
      { label: 'Signed in as', value: user.username },
      { label: 'Organisation', value: organisation },
      { label: 'Scope', value: scope },
    ];
  });

  /**
   * Searching narrows the FAQ to the matching questions and opens each of them.
   */
  protected onQuery(query: string): void {
    this.query.set(query);
    if (query.trim()) this.open.set(this.subjects().flatMap((subject) => subject.items.map((item) => item.id)));
  }

  protected clearSearch(): void {
    this.onQuery('');
  }

  /**
   * Sending feedback hands it to the support team and empties the form for the next one.
   */
  protected sendFeedback(payload: EsnFeedbackPayload): void {
    // TODO(api): send feedback — no endpoint exists yet; the payload is only logged.
    console.info('[help-center] feedback', { ...payload, replyTo: this.replyTo() });
    this.toast.success('Thanks — your feedback is on its way to the support team.');
    this.feedbackSubject.set('');
    this.feedbackDetails.set('');
  }

  protected startTour(): void {
    this.tour.start();
  }
}
